import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  RadarChart, Radar, PolarGrid, PolarAngleAxis,
  PieChart, Pie, Cell, ResponsiveContainer,
  BarChart, Bar, XAxis, YAxis, Tooltip, LabelList,
} from 'recharts';
import { FiArrowRight, FiCompass, FiChevronDown, FiChevronUp, FiCheck, FiSend, FiBookmark, FiFileText, FiUser, FiActivity, FiEdit3, FiSearch, FiMic, FiBarChart } from 'react-icons/fi';
import { dashboardApi, professionalProfileApi, diagnosisApi, careerDirectionApi, resumeApi } from '../services/api';
import LoadingSpinner from '../components/LoadingSpinner';
import { useAuth } from '../contexts/AuthContext';
import KpiCards from '../components/dashboard/KpiCards';
import JobSearchJourney from '../components/dashboard/JobSearchJourney';
// ExtensionBanner removed to save vertical space
import ResumeUploadTooltip from '../components/dashboard/ResumeUploadTooltip';

function fmtDate(d) {
  if (!d) return '';
  return new Date(d).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

const BREAKDOWN_ORDER = [
  'skill_coverage', 'clarity', 'impact_quantification',
  'structural_completeness', 'professional_relevance', 'narrative_strength',
];
const BREAKDOWN_LABELS = {
  skill_coverage: 'Skills',
  clarity: 'Clarity',
  impact_quantification: 'Impact',
  structural_completeness: 'Structure',
  professional_relevance: 'Relevance',
  narrative_strength: 'Narrative',
};
const BREAKDOWN_COLORS = {
  skill_coverage: '#1e40af',
  clarity: '#059669',
  impact_quantification: '#d97706',
  structural_completeness: '#7c3aed',
  professional_relevance: '#047857',
  narrative_strength: '#be185d',
};


function ResumeGauge({ score = 0 }) {
  const color = score >= 70 ? '#059669' : score >= 40 ? '#d97706' : '#dc2626';
  const data = [{ value: score, fill: color }, { value: 100 - score, fill: '#f1f5f9' }];
  return (
    <div style={{ position: 'relative', width: 88, height: 88, flexShrink: 0 }}>
      <ResponsiveContainer width="100%" height="100%">
        <PieChart>
          <Pie data={data} cx="50%" cy="50%" innerRadius="58%" outerRadius="85%"
            dataKey="value" startAngle={90} endAngle={-270} paddingAngle={0}>
            {data.map((e, i) => <Cell key={i} fill={e.fill} stroke="none" />)}
          </Pie>
        </PieChart>
      </ResponsiveContainer>
      <div style={{ position: 'absolute', inset: 0, display: 'flex',
        alignItems: 'center', justifyContent: 'center', pointerEvents: 'none' }}>
        <span style={{ fontSize: 20, fontWeight: 700, color: '#1e293b' }}>{score}</span>
      </div>
    </div>
  );
}

const EXTENSION_URL = 'https://chromewebstore.google.com/detail/resumemo-job-saver/ikkpnjimfefekoncejhmidjdccllhcej';

function QuickTipsPanel({ userStatus, score, totalApplications }) {
  const tips = [];
  if (!userStatus.hasResume) {
    tips.push({ icon: <FiFileText size={16} color="#0f172a" />, text: 'Upload your first resume to get started', link: '/resumes/upload' });
  } else if (score < 60) {
    tips.push({ icon: <FiActivity size={16} color="#0f172a" />, text: 'Run a diagnosis to find improvement areas', link: '/resume-diagnosis' });
  }
  if (!userStatus.hasProfile) {
    tips.push({ icon: <FiUser size={16} color="#0f172a" />, text: 'Complete your profile for better job matches', link: '/profile' });
  }
  if (!userStatus.hasSavedJobs) {
    tips.push({ icon: <FiBookmark size={16} color="#0f172a" />, text: 'Save jobs using our Chrome extension', href: EXTENSION_URL });
  }
  if (totalApplications === 0 && userStatus.hasResume) {
    tips.push({ icon: <FiSend size={16} color="#0f172a" />, text: 'Start applying — check your saved jobs', link: '/jobs/saved' });
  }
  if (userStatus.hasResume && score >= 60) {
    tips.push({ icon: <FiEdit3 size={16} color="#0f172a" />, text: 'Polish your resume for a specific job posting', link: '/resumes' });
  }
  if (totalApplications > 0) {
    tips.push({ icon: <FiMic size={16} color="#0f172a" />, text: 'Practice interview skills for upcoming roles', link: '/jobs/mock-interview' });
  }
  if (!userStatus.hasDiagnosis && userStatus.hasResume) {
    tips.push({ icon: <FiBarChart size={16} color="#0f172a" />, text: 'Get AI-powered resume insights', link: '/resume-diagnosis' });
  }

  const displayTips = tips.slice(0, 4);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
      {displayTips.map((tip, i) => {
        const content = (
          <div key={i} style={{
            display: 'flex', alignItems: 'center', gap: 10,
            padding: '10px 12px', borderRadius: 8,
            background: '#f8fafc', border: '1px solid #e2e8f0',
            cursor: 'pointer', transition: 'all 0.15s',
          }}
            onMouseEnter={e => { e.currentTarget.style.background = '#f0f9ff'; e.currentTarget.style.borderColor = '#1e40af'; }}
            onMouseLeave={e => { e.currentTarget.style.background = '#f8fafc'; e.currentTarget.style.borderColor = '#e2e8f0'; }}
          >
            <span style={{ flexShrink: 0 }}>{tip.icon}</span>
            <span style={{
              fontSize: 12, color: '#334155', lineHeight: 1.4,
              whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', minWidth: 0,
            }} title={tip.text}>{tip.text}</span>
            <FiArrowRight size={12} color="#1e40af" style={{ marginLeft: 'auto', flexShrink: 0 }} />
          </div>
        );
        return tip.href
          ? <a key={i} href={tip.href} target="_blank" rel="noopener noreferrer" style={{ textDecoration: 'none' }}>{content}</a>
          : <Link key={i} to={tip.link} style={{ textDecoration: 'none' }}>{content}</Link>;
      })}
    </div>
  );
}

export default function Dashboard() {
  const { currentUser } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [fetchError, setFetchError] = useState(null);
  const [stats, setStats] = useState({
    totalResumes: 0, totalJobs: 0,
    applicationStats: { applied: 0, interviewing: 0, offered: 0, rejected: 0 },
  });
  const [resumeScore, setResumeScore] = useState(null);
  const [recentResumes, setRecentResumes] = useState([]);
  const [recentJobs, setRecentJobs] = useState([]);
  const [recentApplications, setRecentApplications] = useState([]);
  const [careerDirections, setCareerDirections] = useState(null);
  const [userStatus, setUserStatus] = useState({
    hasResume: false, hasProfile: false, hasApplications: false, hasDiagnosis: false,
  });
  const [setupOpen, setSetupOpen] = useState(false);
  const setupInitRef = useRef(false);
  const [latestDiagnosis, setLatestDiagnosis] = useState(null);
  const [diagnosesList, setDiagnosesList] = useState([]);
  const [selectedDiagnosisId, setSelectedDiagnosisId] = useState(null);

  useEffect(() => {
    async function load() {
      try {
        setFetchError(null);

        const [statsRes, profilesRes, diagLatestRes, diagListRes, resumesRes] = await Promise.allSettled([
          dashboardApi.getStats(),
          professionalProfileApi.listProfiles(),
          diagnosisApi.getLatest(),
          dashboardApi.getDiagnosesList(),
          resumeApi.getAllResumes(),
        ]);

        const data = statsRes.status === 'fulfilled' ? statsRes.value.data : {};
        const s = data.stats || stats;
        setStats(s);
        setResumeScore(data.resumeScore ?? null);
        setRecentResumes(data.recentResumes || []);
        setRecentJobs(data.recentJobs || []);
        setRecentApplications(data.recentApplications || []);

        const appStats = s.applicationStats || {};
        const hasApplications = (appStats.applied + appStats.interviewing + appStats.offered) > 0;

        let hasProfile = false;
        if (profilesRes.status === 'fulfilled') {
          const profiles = profilesRes.value?.data || [];
          hasProfile = profiles.length > 0;
        }

        let dirs = null;
        if (resumesRes.status === 'fulfilled') {
          const resumeList = Array.isArray(resumesRes.value?.data)
            ? resumesRes.value.data
            : resumesRes.value?.data?.resumes ?? [];
          if (resumeList.length > 0) {
            const firstResumeId = resumeList[0]._id || resumeList[0].id;
            try {
              const cdRes = await careerDirectionApi.getForResume(firstResumeId);
              if (cdRes.data && cdRes.data.directions && cdRes.data.directions.length > 0) {
                dirs = cdRes.data.directions.slice(0, 4).map((d, i) => ({
                  name: d.title || d.name || `Direction ${i + 1}`,
                  value: Math.round(d.match_score ?? d.relevance_score ?? 0),
                }));
              }
            } catch (_) { /* no directions yet */ }
          }
        }
        setCareerDirections(dirs);

        let hasDiagnosis = false;
        if (diagLatestRes.status === 'fulfilled' && diagLatestRes.value.data) {
          setLatestDiagnosis(diagLatestRes.value.data);
          hasDiagnosis = true;
        }

        if (diagListRes.status === 'fulfilled' && diagListRes.value.data?.diagnoses) {
          const list = diagListRes.value.data.diagnoses;
          setDiagnosesList(list);
          if (list.length > 0 && !selectedDiagnosisId) {
            setSelectedDiagnosisId(list[0].resumeId);
          }
        }

        const hasSavedJobs = (data.recentJobs || []).length > 0 || (s.savedJobs || 0) > 0;
        const hasCheckedFit = !!data.hasCheckedFit;
        const latestSavedJobId = (data.recentJobs || [])[0]?._id || (data.recentJobs || [])[0]?.id || null;
        const latestResumeId = (data.recentResumes || [])[0]?._id || (data.recentResumes || [])[0]?.id || null;
        const checkedFitJobIds = new Set(data.checkedFitJobIds || []);
        const appliedJobIdsSet = new Set(data.appliedJobIds || []);
        const polishedJobIdsSet = new Set(data.polishedJobIds || []);
        const trackedJobIdsSet = new Set(data.trackedJobIds || []);
        const recentJobsForCycle = (data.recentJobs || []).slice(0, 2).map(j => {
          const id = j._id || j.id || '';
          return {
            id,
            title: j.title || '',
            company: j.company || '',
            hasCheckedFit: checkedFitJobIds.has(id),
            hasPolished: polishedJobIdsSet.has(id),
            hasApplied: appliedJobIdsSet.has(id),
            hasTracked: trackedJobIdsSet.has(id),
          };
        });
        const hasResume = (s.totalResumes || 0) > 0;
        setUserStatus({
          hasResume, hasProfile, hasApplications, hasDiagnosis, hasSavedJobs, hasCheckedFit,
          latestSavedJobId, latestResumeId, recentJobsForCycle,
        });
        if (!setupInitRef.current) {
          setupInitRef.current = true;
          const allSetupDone = hasResume && hasProfile && hasDiagnosis && !!(dirs && dirs.length > 0);
          setSetupOpen(!allSetupDone);
        }
      } catch (e) {
        console.error(e);
        setFetchError(e?.response?.data?.message || e?.message || 'Failed to load dashboard data.');
      } finally {
        setLoading(false);
      }
    }
    load();

    const onVisible = () => { if (document.visibilityState === 'visible') load(); };
    document.addEventListener('visibilitychange', onVisible);
    return () => document.removeEventListener('visibilitychange', onVisible);
  }, []);

  if (loading) return <LoadingSpinner message="Loading dashboard…" />;

  if (fetchError) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[400px] gap-4">
        <div className="bg-red-50 border border-red-200 rounded-xl p-6 text-center max-w-md">
          <p className="text-red-700 font-semibold text-lg mb-2">Unable to load dashboard</p>
          <p className="text-red-600 text-sm mb-6">{fetchError}</p>
          <button type="button" onClick={() => window.location.reload()} className="btn-primary btn-sm">
            Try Again
          </button>
        </div>
      </div>
    );
  }

  const selectedDiag = diagnosesList.find(d => d.resumeId === selectedDiagnosisId);
  const activeDiagnosis = selectedDiag || latestDiagnosis;
  const breakdown = selectedDiag?.resumeBreakdown ?? latestDiagnosis?.resume_breakdown ?? resumeScore?.breakdown;
  const radarData = BREAKDOWN_ORDER
    .filter(k => breakdown?.[k])
    .map(k => ({
      axis: BREAKDOWN_LABELS[k],
      value: breakdown[k].max
        ? Math.round((breakdown[k].score / breakdown[k].max) * 100)
        : 0,
      color: BREAKDOWN_COLORS[k],
    }));

  const radarDisplay = radarData.length > 0 ? radarData : [
    { axis: 'Skill Coverage', value: 0 },
    { axis: 'Clarity', value: 0 },
    { axis: 'Impact', value: 0 },
    { axis: 'Structure', value: 0 },
    { axis: 'Relevance', value: 0 },
    { axis: 'Narrative', value: 0 },
  ];

  const careerPct = careerDirections && careerDirections.length > 0
    ? Math.max(...careerDirections.map(d => d.value))
    : 0;

  const score = selectedDiag?.overallScore ?? latestDiagnosis?.overall_score ?? resumeScore?.score ?? 0;

  const rawHistory = selectedDiag?.scoreHistory || latestDiagnosis?.scoreHistory || [];
  const scoreTimeline = rawHistory.slice(-8).map(entry => ({
    name: fmtDate(entry.date),
    score: entry.score ?? 0,
  }));

  const feedItems = [
    ...recentApplications.slice(0, 3).map(a => ({
      icon: <FiSend size={14} color="#059669" />, text: `Applied to ${a.title || 'role'}${a.company ? ` at ${a.company}` : ''}`,
      date: a.appliedAt, type: 'application',
    })),
    ...recentJobs.slice(0, 2).map(j => ({
      icon: <FiBookmark size={14} color="#0f172a" />, text: `Saved ${j.title || 'job'} at ${j.company || 'Company'}`,
      date: j.createdAt, type: 'job',
    })),
  ].sort((a, b) => new Date(b.date) - new Date(a.date)).slice(0, 5);

  const hasCareerDirection = !!(careerDirections && careerDirections.length > 0);
  const setupSteps = [
    { name: 'Upload Resume', icon: <FiFileText size={12} />, done: userStatus.hasResume, path: '/resumes/upload' },
    { name: 'Build Profile', icon: <FiUser size={12} />, done: userStatus.hasProfile, path: '/profile' },
    { name: 'Run Diagnosis', icon: <FiActivity size={12} />, done: userStatus.hasDiagnosis, path: '/resume-diagnosis' },
    { name: 'Career Direction', icon: <FiCompass size={12} />, done: hasCareerDirection, path: '/career-direction' },
  ];
  const setupDone = setupSteps.filter(s => s.done).length;

  const card = {
    background: '#fff', borderRadius: 12, padding: 14,
    boxShadow: '0 1px 4px rgba(0,0,0,0.07)', marginBottom: 12,
  };
  const sectionLabel = {
    fontSize: 11, fontWeight: 700, color: '#94a3b8',
    textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 8,
  };

  return (
    <>
      <ResumeUploadTooltip hasResume={(stats.totalResumes || 0) > 0} />
      <div style={{
        background: '#f8fafc', height: '100%', padding: '12px 16px',
        overflowY: 'auto', display: 'flex', flexDirection: 'column', boxSizing: 'border-box',
      }}
      >
        <div style={{ display: 'grid', gridTemplateColumns: '1.5fr 3.0fr 1fr', gap: 12, flex: 1, minHeight: 0 }}>
          <div style={{
            display: 'flex', flexDirection: 'column', overflow: 'hidden',
            background: '#fff', borderRadius: 16, padding: 10,
            border: '1px solid #e2e8f0',
            boxShadow: '0 1px 4px rgba(0,0,0,0.07)',
          }}
          >
            <h2 style={{ fontSize: 13, fontWeight: 700, color: '#1e293b', margin: '0 0 8px 0' }}>Progress Status</h2>
            {diagnosesList.length > 0 && (
              <div style={{ marginBottom: 10, flexShrink: 0 }}>
                <select
                  value={selectedDiagnosisId || ''}
                  onChange={e => setSelectedDiagnosisId(e.target.value)}
                  style={{
                    width: '100%', padding: '7px 10px', borderRadius: 8,
                    border: '1px solid #e2e8f0', background: '#f8fafc',
                    fontSize: 12, color: '#334155', fontWeight: 600,
                    cursor: 'pointer', outline: 'none',
                    appearance: 'none',
                    backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 24 24' fill='none' stroke='%2394a3b8' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3E%3Cpolyline points='6 9 12 15 18 9'%3E%3C/polyline%3E%3C/svg%3E")`,
                    backgroundRepeat: 'no-repeat',
                    backgroundPosition: 'right 10px center',
                    paddingRight: 28,
                  }}
                >
                  {diagnosesList.map(d => (
                    <option key={d.resumeId} value={d.resumeId}>
                      {d.resumeName} — {d.overallScore ?? '?'}/100 · {fmtDate(d.updatedAt)}
                    </option>
                  ))}
                </select>
              </div>
            )}
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8, flex: 1, minHeight: 0, overflow: 'hidden' }}>
              <div style={{
                background: '#fff', borderRadius: 12, padding: '14px 14px 12px',
                boxShadow: '0 1px 4px rgba(0,0,0,0.07)', borderTop: '3px solid #059669',
                flexShrink: 0,
              }}
              >
                <p style={{ ...sectionLabel, color: '#059669', marginBottom: 8 }}>Resume Score + Insights Map</p>
                <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
                  <Link
                    to={activeDiagnosis ? '/resume-diagnosis/report' : '/resume-diagnosis'}
                    state={activeDiagnosis ? { diagnosis: activeDiagnosis } : undefined}
                    style={{ textDecoration: 'none', flexShrink: 0 }}
                  >
                    <div style={{
                      display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4,
                      background: '#f8fafc', borderRadius: 10, padding: '8px 10px',
                      border: '1px solid #e2e8f0', cursor: 'pointer', transition: 'box-shadow 0.15s',
                    }}
                      onMouseEnter={e => { e.currentTarget.style.boxShadow = '0 2px 8px rgba(5,150,105,0.2)'; }}
                      onMouseLeave={e => { e.currentTarget.style.boxShadow = 'none'; }}
                    >
                      <span style={{
                        background: '#059669', borderRadius: 6, padding: '2px 8px',
                        fontSize: 10, fontWeight: 700, color: '#fff',
                      }}>Resume Score
                      </span>
                      <ResumeGauge score={score} />
                      <span style={{ fontSize: 10, color: '#059669', fontWeight: 600 }}>
                        {activeDiagnosis ? 'View →' : 'Diagnose →'}
                      </span>
                    </div>
                  </Link>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    {breakdown ? (
                      <ResponsiveContainer width="100%" height={140}>
                        <RadarChart cx="50%" cy="50%" outerRadius="55%" data={radarDisplay}>
                          <PolarGrid stroke="#e2e8f0" />
                          <PolarAngleAxis dataKey="axis" tick={{ fontSize: 9, fill: '#64748b' }} />
                          <Radar dataKey="value" stroke="#059669" fill="#059669" fillOpacity={0.3} />
                        </RadarChart>
                      </ResponsiveContainer>
                    ) : (
                      <div style={{
                        height: 140, display: 'flex', flexDirection: 'column',
                        alignItems: 'center', justifyContent: 'center', color: '#94a3b8', gap: 6,
                      }}
                      >
                        <FiFileText size={22} color="#94a3b8" />
                        <span style={{ fontSize: 11, textAlign: 'center' }}>Upload a resume to see insights</span>
                        <Link to="/resumes/upload" style={{ fontSize: 11, color: '#059669', fontWeight: 600 }}>Upload now →</Link>
                      </div>
                    )}
                  </div>
                </div>
              </div>
              <div style={{
                background: '#fff', borderRadius: 12, padding: '14px',
                boxShadow: '0 1px 4px rgba(0,0,0,0.07)', borderTop: '3px solid #16a34a',
                flexShrink: 0,
              }}
              >
                <Link
                  to={selectedDiagnosisId ? `/resumes/${selectedDiagnosisId}` : '/resumes'}
                  style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 6, textDecoration: 'none' }}
                >
                  <p style={{ ...sectionLabel, color: '#16a34a', marginBottom: 0 }}>Resume Improved</p>
                  <span style={{ fontSize: 10, color: '#16a34a', fontWeight: 600 }}>Edit Resume →</span>
                </Link>
                {scoreTimeline.length > 0 && (
                  <ResponsiveContainer width="100%" height={68}>
                    <BarChart data={scoreTimeline} barSize={10}>
                      <XAxis dataKey="name" tick={{ fontSize: 10, fill: '#94a3b8' }} axisLine={false} tickLine={false} />
                      <YAxis hide domain={[0, 100]} />
                      <Tooltip contentStyle={{ fontSize: 11 }} formatter={(v) => [v, 'Score']} />
                      <Bar dataKey="score" fill="#059669" radius={[4, 4, 0, 0]}>
                        <LabelList dataKey="score" position="top" style={{ fontSize: 9, fill: '#334155', fontWeight: 600 }} />
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                )}
              </div>
              <div style={{
                background: '#fff', borderRadius: 12, padding: '14px',
                boxShadow: '0 1px 4px rgba(0,0,0,0.07)', borderTop: '3px solid #3b82f6',
                flex: 1, minHeight: 0, overflow: 'auto',
              }}
              >
                <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', marginBottom: 6 }}>
                  <p style={{ ...sectionLabel, color: '#1d4ed8', marginBottom: 0 }}>Career Direction</p>
                  {careerPct > 0 && (
                    <span style={{ color: '#0284c7', fontWeight: 700, fontSize: 13 }}>{careerPct}% <span style={{ fontWeight: 500, fontSize: 10, color: '#0369a1' }}>top match</span></span>
                  )}
                </div>
                {careerDirections && careerDirections.length > 0 ? (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                    {careerDirections.map((d, i) => (
                      <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        <span className="info-tip-wrap" title={d.name} style={{
                          fontSize: 9, color: '#64748b', flexShrink: 0, width: 80,
                          overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                          textAlign: 'right', position: 'relative', cursor: 'help',
                        }}>
                          {d.name}
                          <span className="info-tip-text" style={{
                            display: 'none', position: 'absolute', bottom: '100%', right: 0,
                            marginBottom: 8, padding: '6px 10px',
                            background: '#1e293b', color: '#fff', fontSize: 11, lineHeight: 1.4,
                            borderRadius: 6, whiteSpace: 'nowrap', zIndex: 20, pointerEvents: 'none',
                            boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                            border: '1px solid #374151',
                          }}>{d.name}</span>
                        </span>
                        <div style={{ flex: 1, height: 8, background: '#f1f5f9', borderRadius: 4, overflow: 'hidden' }}>
                          <div style={{
                            width: `${d.value}%`, height: '100%', borderRadius: 4,
                            background: ['#1e40af', '#2563eb', '#3b82f6', '#60a5fa'][Math.min(i, 3)],
                          }} />
                        </div>
                        <span style={{ fontSize: 9, color: '#64748b', flexShrink: 0, width: 24, textAlign: 'right' }}>{d.value}%</span>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center',
                    gap: 6, padding: '12px 0', color: '#94a3b8' }}
                  >
                    <FiCompass size={20} style={{ color: '#cbd5e1' }} />
                    <span style={{ fontSize: 12, textAlign: 'center' }}>Pick a Direction First</span>
                    <Link to="/career-direction" style={{ fontSize: 11, color: '#1e40af', fontWeight: 600 }}>
                      Go to Career Direction →
                    </Link>
                  </div>
                )}
              </div>
            </div>
          </div>

          <div style={{
            display: 'flex', flexDirection: 'column', overflow: 'hidden',
            background: '#fff', borderRadius: 16, padding: 10,
            border: '1px solid #e2e8f0',
            boxShadow: '0 1px 4px rgba(0,0,0,0.07)',
          }}
          >
            <h2 style={{ fontSize: 13, fontWeight: 700, color: '#1e293b', margin: '0 0 8px 0' }}>Activity Journal</h2>
            <div style={{ flexShrink: 0, marginBottom: 8, borderRadius: 10, overflow: 'hidden', border: '1px solid #e2e8f0', background: '#fff' }}>
              <button type="button" onClick={() => setSetupOpen(p => !p)} style={{
                display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                width: '100%', padding: '7px 12px', background: 'none', border: 'none', cursor: 'pointer',
              }}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                  <span style={{ fontSize: 11, fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.06em' }}>
                    One-Time Setup
                  </span>
                  <span style={{
                    fontSize: 10, fontWeight: 700, padding: '1px 6px', borderRadius: 10,
                    background: setupDone === 4 ? '#dcfce7' : '#fef3c7',
                    color: setupDone === 4 ? '#16a34a' : '#d97706',
                  }}
                  >
                    {setupDone}/4
                  </span>
                  {setupDone === 4 && <FiCheck size={11} color="#16a34a" />}
                </div>
                {setupOpen ? <FiChevronUp size={13} color="#94a3b8" /> : <FiChevronDown size={13} color="#94a3b8" />}
              </button>
              {setupOpen && (
                <div style={{ padding: '6px 12px 10px', borderTop: '1px solid #f1f5f9', display: 'flex', gap: 6 }}>
                  {setupSteps.map((step, i) => (
                    <Link key={i} to={step.path} style={{ textDecoration: 'none', flex: 1 }}>
                      <div style={{
                        display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4,
                        padding: '6px 4px', borderRadius: 8, border: `1px solid ${step.done ? '#bbf7d0' : '#f1f5f9'}`,
                        background: step.done ? '#f0fdf4' : '#fafafa',
                        transition: 'box-shadow 0.15s',
                      }}
                        onMouseEnter={e => { e.currentTarget.style.boxShadow = '0 2px 8px rgba(0,0,0,0.08)'; }}
                        onMouseLeave={e => { e.currentTarget.style.boxShadow = 'none'; }}
                      >
                        <div style={{
                          width: 22, height: 22, borderRadius: '50%', flexShrink: 0,
                          background: step.done ? '#16a34a' : '#e2e8f0',
                          display: 'flex', alignItems: 'center', justifyContent: 'center',
                        }}
                        >
                          {step.done
                            ? <FiCheck size={11} color="#fff" />
                            : <span style={{ fontSize: 10, color: '#0f172a' }}>{step.icon}</span>}
                        </div>
                        <span style={{
                          fontSize: 10, fontWeight: 600, textAlign: 'center', lineHeight: 1.3,
                          color: step.done ? '#16a34a' : '#64748b',
                        }}
                        >
                          {step.name}
                        </span>
                      </div>
                    </Link>
                  ))}
                </div>
              )}
            </div>
            <div style={{ flexShrink: 0, marginBottom: 8 }}>
              <JobSearchJourney userStatus={userStatus} />
            </div>
            <div style={{ flexShrink: 0, marginBottom: 8 }}>
              <KpiCards stats={stats} />
            </div>
            <div style={{
              ...card, background: '#fff',
              flex: 2, minHeight: 0, overflow: 'auto', marginBottom: 0, padding: '12px 16px',
            }}
            >
              <div style={{ marginBottom: 8 }}>
                <p style={{ ...sectionLabel, marginBottom: 0 }}>Activity Feed</p>
              </div>
              <div style={{ display: 'flex', gap: 16, alignItems: 'flex-start' }}>
                <div style={{ flex: 1, minWidth: 0 }}>
                  {feedItems.length === 0 ? (
                    <p style={{ fontSize: 12, color: '#94a3b8', margin: 0 }}>No save or apply activity yet.</p>
                  ) : (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                      {feedItems.map((item, i) => (
                        <div key={i} style={{
                          display: 'flex', alignItems: 'center', gap: 8,
                          padding: '6px 0', borderBottom: '1px solid #f1f5f9',
                        }}>
                          <span style={{ flexShrink: 0 }}>{item.icon}</span>
                          <span style={{
                            fontSize: 12, color: '#334155', fontWeight: 500,
                            whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: '100%',
                          }} title={item.text}>
                            {item.text}
                          </span>
                          {item.date && (
                            <span style={{ color: '#94a3b8', fontSize: 10, flexShrink: 0, marginLeft: 'auto' }}>
                              {fmtDate(item.date)}
                            </span>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
                <ActivityHeatmap
                  recentResumes={recentResumes}
                  recentJobs={recentJobs}
                  recentApplications={recentApplications}
                />
              </div>
            </div>
          </div>

          <div style={{
            display: 'flex', flexDirection: 'column', overflow: 'hidden',
            background: '#fff', borderRadius: 16, padding: 10,
            border: '1px solid #e2e8f0',
            boxShadow: '0 1px 4px rgba(0,0,0,0.07)',
          }}
          >
            <p style={{ fontSize: 13, fontWeight: 700, color: '#1e293b', marginBottom: 8 }}>Quick Tips</p>
            <div style={{ display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0, overflow: 'hidden' }}>
              <QuickTipsPanel
                userStatus={userStatus}
                score={score}
                totalApplications={(stats?.applicationStats?.applied || 0) + (stats?.applicationStats?.interviewing || 0)}
              />
              <div style={{ marginTop: 12, borderTop: '1px solid #e2e8f0', paddingTop: 12 }}>
                <p style={{ fontSize: 10, fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase',
                  letterSpacing: '0.1em', marginBottom: 8 }}
                >Quick Actions
                </p>
                {[
                  { label: 'Polish my resume', icon: <FiEdit3 size={12} />, link: '/resumes' },
                  { label: 'Find matching jobs', icon: <FiSearch size={12} />, link: '/jobs/saved' },
                  { label: 'Practice interview', icon: <FiMic size={12} />, link: '/jobs/mock-interview' },
                ].map((a) => {
                  const inner = (
                    <div
                      key={a.label}
                      style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                        padding: '7px 11px', borderRadius: 8, marginBottom: 5,
                        background: '#fff', border: '1px solid #e2e8f0',
                        cursor: 'pointer', transition: 'all 0.15s' }}
                      onMouseEnter={e => { e.currentTarget.style.background = '#f0fdf4'; e.currentTarget.style.borderColor = '#bbf7d0'; }}
                      onMouseLeave={e => { e.currentTarget.style.background = '#fff'; e.currentTarget.style.borderColor = '#e2e8f0'; }}
                    >
                      <span style={{ fontSize: 12, color: '#475569', display: 'flex', alignItems: 'center', gap: 8 }}>
                        <span style={{ color: '#0f172a' }}>{a.icon}</span>
                        {a.label}
                      </span>
                      <FiArrowRight size={11} color="#1e40af" />
                    </div>
                  );
                  return a.href
                    ? <a key={a.label} href={a.href} target="_blank" rel="noopener noreferrer" style={{ textDecoration: 'none' }}>{inner}</a>
                    : <Link key={a.label} to={a.link} style={{ textDecoration: 'none' }}>{inner}</Link>;
                })}
              </div>
              <div style={{
                marginTop: 10,
                background: 'linear-gradient(135deg,#5AAFA7,#6366f1)',
                borderRadius: 10, padding: '12px 14px', textAlign: 'center',
              }}
              >
                <p style={{ margin: 0, color: '#fff', fontSize: 12, fontWeight: 700, lineHeight: 1.7 }}>
                  {currentUser?.full_name?.split(' ')[0] || 'Hey'}, keep building your profile to unlock better matches.
                </p>
                <button type="button" onClick={() => navigate('/career-direction')} style={{
                  marginTop: 8, padding: '5px 14px', borderRadius: 7,
                  background: 'rgba(255,255,255,0.2)', border: '1px solid rgba(255,255,255,0.35)',
                  color: '#fff', fontSize: 11, cursor: 'pointer', fontWeight: 700,
                  letterSpacing: '0.03em',
                }}>Explore Career Direction →
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}

function ActivityHeatmap({ recentResumes, recentJobs, recentApplications }) {
  const actMap = {};
  const mark = (date) => {
    if (!date) return;
    const d = new Date(date);
    if (Number.isNaN(d.getTime())) return;
    const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
    actMap[key] = (actMap[key] || 0) + 1;
  };
  recentJobs.forEach(j => mark(j.createdAt));
  recentApplications.forEach(a => mark(a.appliedAt));

  const today = new Date();
  const year = today.getFullYear();
  const month = today.getMonth();
  const firstDow = new Date(year, month, 1).getDay();
  const todayKey = `${year}-${String(month + 1).padStart(2, '0')}-${String(today.getDate()).padStart(2, '0')}`;

  const colorForCount = (n) => {
    if (n === 0) return '#b8ccb8';
    if (n === 1) return '#4ade80';
    if (n === 2) return '#16a34a';
    return '#14532d';
  };

  const CELL = 16;
  const GAP = 3;
  const dayLabels = ['S', 'M', 'T', 'W', 'T', 'F', 'S'];
  const cells = [];
  for (let i = 0; i < firstDow; i += 1) cells.push(null);
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  for (let d = 1; d <= daysInMonth; d += 1) cells.push(d);

  const weeks = [];
  for (let i = 0; i < cells.length; i += 7) weeks.push(cells.slice(i, i + 7));

  return (
    <div style={{ flexShrink: 0 }}>
      <div style={{ fontSize: 10, color: '#6b7280', fontWeight: 600, marginBottom: 4, textAlign: 'right' }}>
        {today.toLocaleString('default', { month: 'short', year: 'numeric' })}
      </div>
      <div style={{ display: 'flex', gap: GAP, marginBottom: GAP }}>
        {dayLabels.map((d, i) => (
          <div key={i} style={{ width: CELL, fontSize: 9, color: '#6b7280', textAlign: 'center', fontWeight: 600 }}>{d}</div>
        ))}
      </div>
      {weeks.map((week, wi) => (
        <div key={wi} style={{ display: 'flex', gap: GAP, marginBottom: GAP }}>
          {[...Array(7)].map((_, di) => {
            const day = week[di];
            if (day == null) return <div key={di} style={{ width: CELL, height: CELL }} />;
            const key = `${year}-${String(month + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
            const count = actMap[key] || 0;
            const isToday = key === todayKey;
            return (
              <div
                key={di}
                title={`${day} — ${count} activit${count !== 1 ? 'ies' : 'y'}`}
                style={{
                  width: CELL, height: CELL, borderRadius: 3,
                  background: colorForCount(count),
                  outline: isToday ? '2px solid #15803d' : 'none',
                  outlineOffset: 1, cursor: 'default',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  fontSize: 9, color: count > 0 ? '#fff' : '#4b5563', fontWeight: 600,
                }}
              >
                {day}
              </div>
            );
          })}
        </div>
      ))}
      <div style={{ display: 'flex', alignItems: 'center', gap: 3, marginTop: 4, justifyContent: 'flex-end' }}>
        <span style={{ fontSize: 9, color: '#6b7280' }}>Less</span>
        {['#b8ccb8', '#4ade80', '#16a34a', '#14532d'].map((c, i) => (
          <div key={i} style={{ width: 10, height: 10, borderRadius: 2, background: c }} />
        ))}
        <span style={{ fontSize: 9, color: '#6b7280' }}>More</span>
      </div>
    </div>
  );
}
